import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Col, Container, Row } from "reactstrap";
import {
  DATABASE_CLIENT_PAGE,
  DATABASE_VEHICULE_PAGE,
  FERRAILLEURS_ENCHERES_VISIBLES_PAGE,
  PRAMETRAGE_PAGE,
  RESEAU_PAGE,
  VHU_LOTS_PAGE,
  VVINAL_PAGE,
} from "../../routes/routeConstants";
import {
  ASSURANCE_ROLES,
  EXPERT_ROLES,
  FERRAILLEUR_ROLES,
  getCurrentRoles,
  hasAnyRole,
} from "../../helpers/roles";

const modules = [
  {
    title: "Base véhicules",
    description: "Consultez, importez et maintenez le référentiel complet des véhicules.",
    icon: "ri-car-line",
    accent: "blue",
    path: DATABASE_VEHICULE_PAGE,
    meta: "Référentiel",
    roles: [...EXPERT_ROLES, ...ASSURANCE_ROLES],
  },
  {
    title: "Clients",
    description: "Centralisez les profils clients et toutes leurs informations utiles.",
    icon: "ri-user-3-line",
    accent: "green",
    path: DATABASE_CLIENT_PAGE,
    meta: "Gestion",
    roles: [...EXPERT_ROLES, ...ASSURANCE_ROLES],
  },
  {
    title: "Valeur vénale",
    description: "Calculez et vérifiez les valeurs selon les critères métier configurés.",
    icon: "ri-currency-line",
    accent: "amber",
    path: VVINAL_PAGE,
    meta: "Évaluation",
    roles: EXPERT_ROLES,
  },
  {
    title: "Paramétrage",
    description: "Administrez les coefficients, barèmes et valeurs de référence.",
    icon: "ri-settings-3-line",
    accent: "slate",
    path: PRAMETRAGE_PAGE,
    meta: "Configuration",
    roles: EXPERT_ROLES,
  },
  {
    title: "Lots VHU",
    description: "Gérez les lots multi-véhicules hors usage et suivez toutes les offres.",
    icon: "ri-recycle-line",
    accent: "amber",
    path: VHU_LOTS_PAGE,
    meta: "VHU",
    roles: EXPERT_ROLES,
  },
  {
    title: "Ferrailleurs",
    description: "Consultez les enchères visibles, les achats et les conditions.",
    icon: "ri-user-star-line",
    accent: "green",
    path: FERRAILLEURS_ENCHERES_VISIBLES_PAGE,
    meta: "Portail",
    roles: FERRAILLEUR_ROLES,
  },
  {
    title: "Réseau",
    description: "Administrez les groupes et les profils de ferrailleurs partenaires.",
    icon: "ri-team-line",
    accent: "slate",
    path: RESEAU_PAGE,
    meta: "Ciblage",
    roles: EXPERT_ROLES,
  },
];

function ModuleSelectorPage() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const userRoles = useMemo(() => getCurrentRoles(), []);
  const visibleModules = useMemo(
    () => modules.filter((module) => hasAnyRole(userRoles, module.roles)),
    [userRoles]
  );

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  const username = useMemo(() => {
    const authUser = localStorage.getItem("authUser");
    if (!authUser) return "Admin";

    try {
      const user = JSON.parse(authUser);
      const displayValue =
        user?.name ||
        user?.username ||
        user?.email ||
        user?.user?.name ||
        user?.user?.username ||
        user?.user?.email;

      if (!displayValue) return "Admin";

      const cleanName = displayValue.includes("@")
        ? displayValue.split("@")[0]
        : displayValue;

      return cleanName
        .split(/[._\s-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    } catch (error) {
      console.error("Error parsing authUser from localStorage", error);
      return "Admin";
    }
  }, []);

  const formattedTime = currentTime.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const formattedDate = currentTime.toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return (
    <div className="page-content module-selector-page">
      <div className="module-selector-orb module-selector-orb-one" aria-hidden="true" />
      <div className="module-selector-orb module-selector-orb-two" aria-hidden="true" />

      <Container fluid className="module-selector-shell">
        <header className="module-selector-topbar">
          <div className="module-selector-brand">
            <img src="/ja-eval_logo_horizontal_transparent.png" alt="JA-Eval" />
            <span>Plateforme de gestion des épaves</span>
          </div>

          <div className="module-selector-session">
            <div className="module-session-time">
              <i className="ri-calendar-event-line" />
              <div>
                <span>{formattedTime}</span>
                <small>{formattedDate}</small>
              </div>
            </div>
            <div className="module-session-user">
              <span className="module-session-avatar">
                {username.charAt(0).toUpperCase()}
              </span>
              <div>
                <span>{username}</span>
                <small>Session active</small>
              </div>
            </div>
            <Button
              tag={Link}
              to="/logout"
              color="none"
              className="module-session-logout"
              title="Se déconnecter"
              aria-label="Se déconnecter"
            >
              <i className="ri-logout-box-r-line" />
              <span>Déconnexion</span>
            </Button>
          </div>
        </header>

        <main>
          <section className="module-selector-hero">
            <div className="module-selector-hero-copy">
              <span className="module-selector-kicker">
                <i className="ri-layout-grid-line" />
                Espace de travail
              </span>
              <h1>Bonjour {username}, choisissez votre module.</h1>
              <p>
                Accédez rapidement à vos outils pour poursuivre la gestion et le
                suivi de vos dossiers.
              </p>
              <div className="module-selector-highlights">
                <span>
                  <i className="ri-apps-2-line" />
                  {visibleModules.length} module{visibleModules.length > 1 ? "s" : ""} disponible{visibleModules.length > 1 ? "s" : ""}
                </span>
                <span><i className="ri-shield-check-line" />Accès sécurisé</span>
              </div>
            </div>

            <div className="module-selector-hero-visual" aria-hidden="true">
              <span className="module-visual-main"><i className="ri-car-line" /></span>
              <span className="module-visual-item module-visual-item-one"><i className="ri-file-list-3-line" /></span>
              <span className="module-visual-item module-visual-item-two"><i className="ri-line-chart-line" /></span>
              <span className="module-visual-item module-visual-item-three"><i className="ri-recycle-line" /></span>
            </div>
          </section>

          <div className="module-selector-section-heading">
            <div>
              <span>Navigation principale</span>
              <h2>Vos modules</h2>
            </div>
            <p>Sélectionnez un espace pour commencer.</p>
          </div>

          <Row className="module-grid">
            {visibleModules.map((module, index) => (
              <Col xl={3} lg={4} md={6} key={module.title}>
                <Link
                  to={module.path}
                  className={`module-card module-card-${module.accent}`}
                  aria-label={`Ouvrir ${module.title}`}
                >
                  <span className="module-card-number">{String(index + 1).padStart(2, "0")}</span>
                  <span className="module-card-top">
                    <span className="module-card-icon">
                      <i className={module.icon} />
                    </span>
                    <span className="module-card-meta">{module.meta}</span>
                  </span>
                  <span className="module-card-copy">
                    <strong>{module.title}</strong>
                    <span>{module.description}</span>
                  </span>
                  <span className="module-card-action">
                    Accéder au module
                    <i className="ri-arrow-right-line" />
                  </span>
                </Link>
              </Col>
            ))}
          </Row>

          {visibleModules.length === 0 && (
            <div className="module-selector-empty">
              <span><i className="ri-lock-line" /></span>
              <h2>Aucun module disponible</h2>
              <p>Votre profil ne dispose actuellement d’aucun espace accessible.</p>
            </div>
          )}
        </main>

        <footer className="module-selector-footer">
          <span><i className="ri-shield-keyhole-line" />Vos accès sont adaptés à votre profil utilisateur.</span>
          <span>JA-Eval · Gestion sécurisée et traçable</span>
        </footer>
      </Container>
    </div>
  );
}

export default ModuleSelectorPage;
